import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { message } from 'antd';
import './profile-styles.css';

const Profile = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    profilePicture: '',
    gender: '',
    status: '',
    roleId: '',
    address: '',
  });
  const [previewImage, setPreviewImage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      const userType = localStorage.getItem("userType");

      if (!userData?.id) {
        message.error('User data not found');
        return;
      }

      let endpoint = '';
      if (userType === "agent") {
        endpoint = `/agent/${userData.id}`;
      } else if (userType === "subAgent") {
        endpoint = `/subAgent/${userData.id}`;
      } else {
        endpoint = `/user/${userData.id}`;
      }

      const response = await axios.get(endpoint);
      const user = response.data.user;
         
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phoneNumber: user.phoneNumber || user.phone || '',
        profilePicture: user.profilePictureURL || '', 
        gender: user.gender || '',
        status: user.status || '',
        roleId: user.roleId || '',
        address: user.address || '',
      });

      if (user.profilePictureURL) { 
        setPreviewImage(user.profilePictureURL);
      }
    } catch (error) {
      message.error('Failed to fetch user data');
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!/^\d{10,15}$/.test(formData.phoneNumber.replace(/[-()+\s]/g, ''))) {
      newErrors.phoneNumber = 'Invalid phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    if (!file.type.match(/image\/(jpeg|png|gif)/)) {
      message.error('Please upload a valid image file (JPEG, PNG, or GIF)');
      return;
    }
  
    if (file.size > 5 * 1024 * 1024) {
      message.error('Image size should be less than 5MB');
      return;
    }
  
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(file);
  
    // Prepare for upload
    const formDataToSend = new FormData();
    const userData = JSON.parse(localStorage.getItem('userData'));
  
    // Append all required fields
    formDataToSend.append('id', userData.id);
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phoneNumber', formData.phoneNumber);
    formDataToSend.append('gender', formData.gender);
    formDataToSend.append('status', formData.status);
    formDataToSend.append('roleId', formData.roleId);
    formDataToSend.append('address', formData.address);
    formDataToSend.append('profilePicture', file);
  
    try {
      setIsUploading(true);
      const response = await axios.put('/user/' + userData.id, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.id) {
        // Build the updated profile picture URL.
        const updatedProfilePictureURL =
          response.data.profilePictureURL ||
          `${process.env.REACT_APP_BASE_URL}/uploads/profile/${file.name}`;

        // Update state and localStorage
        const updatedUserData = {
          ...userData,
          profilePictureURL: updatedProfilePictureURL,
        };
        setFormData(prev => ({
          ...prev,
          profilePicture: updatedProfilePictureURL,
        }));
        setPreviewImage(updatedProfilePictureURL);
        localStorage.setItem('userData', JSON.stringify(updatedUserData));
        // Dispatch a custom event to notify other components
        window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: updatedUserData }));
        message.success('Profile picture uploaded successfully');
      }
    } catch (error) {
      message.error('Failed to upload image');
      // Reset preview on error
      setPreviewImage(formData.profilePicture);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
  
    setIsLoading(true);
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      const userType = localStorage.getItem("userType");
      let endpoint = '';
  
      if (userType === "agent") {
        endpoint = '/updateAgent';
      } else if (userType === "subAgent") {
        endpoint = '/updateSubAgent';
      } else {
        endpoint = '/user/' + userData.id;
      }
  
      const response = await axios.put(endpoint, {
        id: userData.id,
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        // Keep existing values for fields not updated here
        gender: userData.gender || '',
        status: userData.status || '',
        roleId: userData.roleId || '',
        address: userData.address || '',
      });
  
      if (response.data.id) {
        console.log(response.data, "+++++++++++++++++++++++++++++++++++++++", formData, "---------------", userData);
        
        const updatedUserData = {
          ...userData,
          name: formData.name,
          email: formData.email,
          phoneNumber: formData.phoneNumber,
          profilePictureURL: response.data?.profilePictureURL, // unchanged unless image updated
        };
  
        localStorage.setItem('userData', JSON.stringify(updatedUserData));
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('userDataUpdated', { detail: updatedUserData }));
        setFormData(updatedUserData);
        message.success('Profile updated successfully!');
      }
    } catch (error) {
      message.error(error.response?.data?.msg || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="profile-wrapper">
      <div className="profile-container">
        <div className="profile-header">
          <h2>Profile Settings</h2>
          <p>Update your personal information</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="profile-picture-container">
            <img
              src={previewImage || formData.profilePicture || '/default-avatar.png'}
              alt="Profile"
              className="profile-picture"
            />
            <label className="profile-picture-upload">
              <input
                type="file"
                accept="image/jpeg,image/png,image/gif"
                onChange={handleImageChange}
                disabled={isUploading}
              />
              {isUploading ? (
                <div className="loading-spinner" />
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              )}
            </label>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Full Name</label>
              <div className="input-container">
                <span className="input-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </span>
                <input
                  type="text"
                  name="name"
                  className="form-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter your full name"
                />
              </div>
              {errors.name && <div className="error-message">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label>Email</label>
              <div className="input-container">
                <span className="input-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </span>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && <div className="error-message">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <div className="input-container">
                <span className="input-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                  </svg>
                </span>
                <input
                  type="tel"
                  name="phoneNumber"
                  className="form-input"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Enter your phone number"
                />
              </div>
              {errors.phoneNumber && <div className="error-message">{errors.phoneNumber}</div>}
            </div>
          </div>

          <button type="submit" className="submit-button" disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="loading-spinner" />
                <span>Updating...</span>
              </>
            ) : (
              <>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <span>Save Changes</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Profile;
